import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Container, Grid, Typography, Box } from "@mui/material";
import CartCard from "./CartCard";
import { GlobalContext } from "./../../GlobalProvider";
import CustomButton from "./../../utility-v1/Button";
import { IMAGE_BASE_URL } from "../../config/constants"; // Import IMAGE_BASE_URL
import DeleteIcon from "@mui/icons-material/Delete"; // Import the Delete icon
import IconButton from "@mui/material/IconButton";
import { FONT_FAMILY } from "../../config/themeConstants"; // Import FONT_FAMILY
import { API_BASE_URL } from "../../config/constants";
import StoredCartsUpdate from "./../UtilityClasses/StoredCartsUpdate";

const Cart = ({ onRemove, allItems }) => {
  const [cartItems, setCartItems] = useState([]);
  const [comboCartItems, setComboCartItems] = useState({}); // State for combo cart items as an object
  const [totalCartValue, setTotalCartValue] = useState(0);
  const [totalComboCartValue, setTotalComboCartValue] = useState(0); // State for total combo cart value
  const navigate = useNavigate();
  const { globalVariable, updateGlobalVariable } = useContext(GlobalContext);

  useEffect(() => {
    // Load regular cart items
    updateCartByQuantityLeftCheck();

    // Load combo cart items from localStorage as an object
    const storedComboCart = JSON.parse(localStorage.getItem("comboCart")) || {};
    console.log("Loaded comboCart from localStorage:", storedComboCart); // Debug: log comboCart
    setComboCartItems(storedComboCart);
    calculateTotalComboCartValue(storedComboCart);
  }, []);

  const updateCartByQuantityLeftCheck = async () => {
    const storedCart = StoredCartsUpdate.updateCartByQuantityLeftCheck(
      allItems
    );
    setCartItems(storedCart);
    calculateTotalCartValue(storedCart);
  };

  // Calculate total for regular cart items
  const calculateTotalCartValue = (cart) => {
    let total = 0;
    Object.entries(cart).forEach(([inventoryid, item]) => {
      total += item.price * item.quantity;
    });
    setTotalCartValue(total);
  };

  // Calculate total for combo cart items (now using object)
  const calculateTotalComboCartValue = (comboCart) => {
    let total = 0;
    Object.values(comboCart).forEach((combo) => {
      total += combo.price; // Assuming `combo.price` is already the total for that combo
    });
    setTotalComboCartValue(total);
  };

  const handleRemove = (inventoryid) => {
    const storedCart = JSON.parse(localStorage.getItem("cartItems")) || {};
    delete storedCart[inventoryid];
    setCartItems(storedCart);
    localStorage.setItem("cartItems", JSON.stringify(storedCart));
    updateGlobalVariable("cartItems", storedCart);
    calculateTotalCartValue(storedCart);
    if (onRemove) onRemove();
  };

  const handleRemoveCombo = (comboId) => {
    const storedComboCart = JSON.parse(localStorage.getItem("comboCart")) || {};
    delete storedComboCart[comboId]; // Remove combo by ID

    setComboCartItems(storedComboCart);
    localStorage.setItem("comboCart", JSON.stringify(storedComboCart));
    calculateTotalComboCartValue(storedComboCart);
    console.log("Removed combo:", storedComboCart);
  };

  const updateParent = (inventoryid, newQuantity) => {
    const storedCart = JSON.parse(localStorage.getItem("cartItems")) || {};
    if (storedCart[inventoryid]) {
      if (newQuantity > storedCart[inventoryid].left) {
        // Show an alert or handle the case when newQuantity exceeds difference
        alert(
          `Cannot add more than ${storedCart[inventoryid].left} items to the cart.`
        );
      } else {
        // Update the quantity in storedCart if it is within the limit
        storedCart[inventoryid].quantity = newQuantity;
        // Save the updated cart back to localStorage
        localStorage.setItem("cartItems", JSON.stringify(storedCart));
      }
    }

    setCartItems(storedCart);
    localStorage.setItem("cartItems", JSON.stringify(storedCart));
    updateGlobalVariable("cartItems", storedCart);
    calculateTotalCartValue(storedCart);
  };

  const handleCheckout = () => {
    window.location.href = "/jewels/checkout";
  };

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%", // Full width for small screens
        maxWidth: { md: "50%" }, // Limit width to 50% for large screens
        margin: "auto", // Center content horizontally
        padding: 1, // Add padding for spacing
      }}
    >
      {/* Display regular cart items */}
      {Object.keys(cartItems).length !== 0 &&
        Object.entries(cartItems).map(([inventoryid, item]) => (
          <CartCard
            key={inventoryid}
            image={item.image}
            label={item.label}
            price={item.price}
            quantity={item.quantity}
            itemLeft={item.left}
            handleRemove={() => handleRemove(inventoryid)}
            updateParent={(newQuantity) =>
              updateParent(inventoryid, newQuantity)
            }
          />
        ))}

      {/* Display combo cart items */}
      {Object.keys(comboCartItems).length > 0 && (
        <Box sx={{ mt: 1, width: "100%" }}>
          <Grid container spacing={0}>
            {Object.entries(comboCartItems).map(([comboId, combo]) => (
              <Grid item xs={12} key={comboId}>
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "10px",
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    width: "100%",
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={10}>
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontWeight: "500", // Highlight Total text
                          fontSize: { xs: "17px", lg: "24px" },
                          marginLeft: "10px",
                          color: "teal",
                        }}
                      >
                        {combo.comboname}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "right" }}>
                      <IconButton onClick={() => handleRemoveCombo(comboId)}>
                        <DeleteIcon fontSize="medium" />
                      </IconButton>
                    </Grid>
                    {combo.items.map((item) => (
                      <Grid
                        item
                        xs={6}
                        md={4}
                        key={item.inventoryid}
                        display="flex"
                        justifyContent="center"
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            maxWidth: "180px", // Ensuring consistency in size
                            height: { xs: "180px", lg: "180px" },
                            borderRadius: "8px",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: "10px",
                          }}
                        >
                          {/* Image Section */}
                          <Box
                            component="img"
                            src={IMAGE_BASE_URL + item.image}
                            alt={item.label}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />

                          {/* Label Section */}
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 24, // Adjusted to fit price below
                              left: 10,
                              width: "89%", // Ensures the label box matches the image width
                              bgcolor: "rgba(0, 0, 0, 0.6)",
                              color: "white",
                              textAlign: "center",
                              py: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                px: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Box>

                          {/* Price Section */}
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: "-5px",
                              left: 10,
                              width: "89%",
                              bgcolor: "rgba(34, 139, 34, 0.9)", // Green background for price
                              color: "white",
                              textAlign: "center",
                              py: 0.5,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "14px", fontWeight: "bold" }}
                            >
                              ₹{parseFloat(item.price).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}

                    <Grid item xs={12} sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontWeight: "500",
                          fontSize: { xs: "17px", lg: "24px" },
                          color: "teal",
                          textAlign: "right",
                        }}
                      >
                        <span
                          style={{
                            textDecoration: "line-through",
                            fontSize: "14px",
                            color: "gray",
                            marginRight: "8px",
                          }}
                        >
                          ₹{parseFloat(totalComboCartValue).toFixed(2)}
                        </span>
                        Combo Price:
                        <span
                          style={{
                            fontSize: "20px",
                            fontWeight: "bold",
                            color: "green",
                            marginLeft: "5px",
                          }}
                        >
                          ₹{parseFloat(combo.price).toFixed(2)}
                        </span>
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Total & Buttons */}
      {(Object.keys(cartItems).length > 0 ||
        Object.keys(comboCartItems).length > 0) && (
        <Box sx={{ mt: 3, textAlign: "center", width: "100%" }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "600",
              color: "teal",
              mb: 2,
            }}
          >
            Total Cart Value: ₹
            {(totalCartValue + totalComboCartValue).toFixed(2)}
          </Typography>

          {/* Buttons in a row */}
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={6}>
              <CustomButton
                label="Proceed to Checkout"
                variant="contained"
                color="primary"
                size="large"
                onClick={handleCheckout}
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomButton
                label="Continue Shopping"
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => {
                  window.location.href = "/jewels/list/All";
                }}
                sx={{ width: "100%" }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Empty Cart Message */}
      {Object.keys(cartItems).length === 0 &&
        Object.keys(comboCartItems).length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "25vh",
              textAlign: "center",
              gap: 2,
              padding: 3,
              border: "1px solid #ddd",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Typography variant="h6" component="h2" color="textSecondary">
              Your cart is empty. Start adding items to checkout!
            </Typography>
            <CustomButton
              label="Continue Shopping"
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => navigate("/jewels/list/All")}
              sx={{
                width: "100%",
                fontWeight: 600,
              }}
            />
          </Box>
        )}
    </Container>
  );
};

export default Cart;
